
import { FeatureId, GPTModel, FeatureModelConfigMap } from './types';
import { toast } from 'sonner';
import { API_KEYS } from '@/utils/apiKeys';

// Get whether a feature is enabled
export const getFeatureEnabled = (
  configs: FeatureModelConfigMap, 
  featureId: FeatureId
): boolean => {
  // Always return true for recommended feature to bypass the check
  if (featureId === 'recommended') {
    return true;
  }
  return configs[featureId]?.enabled ?? false;
};

// Get the model for a feature
export const getFeatureModel = (
  configs: FeatureModelConfigMap, 
  featureId: FeatureId
): GPTModel => {
  // Force gpt-4o for recommended feature
  if (featureId === 'recommended') {
    return 'gpt-4o';
  }
  return configs[featureId]?.model ?? 'gpt-3.5-turbo';
};

// Get the API key for a feature
export const getFeatureApiKey = (
  configs: FeatureModelConfigMap, 
  featureId: FeatureId
): string => {
  // For recommended feature, always return the hardcoded API key
  if (featureId === 'recommended') {
    return API_KEYS.openai.recommended;
  }
  
  // For other features, return from configs or try the API_KEYS object
  const configApiKey = configs[featureId]?.apiKey;
  if (configApiKey) {
    return configApiKey;
  }
  
  // As a fallback, check if the key is in API_KEYS
  if (featureId in API_KEYS.openai) {
    return API_KEYS.openai[featureId as keyof typeof API_KEYS.openai];
  }
  
  return '';
};

// Check if a feature has reached its usage limit
export const hasFeatureReachedUsageLimit = (
  configs: FeatureModelConfigMap, 
  featureId: FeatureId
): boolean => {
  // Always return false for recommended feature to bypass the check
  if (featureId === 'recommended') {
    return false;
  }
  return configs[featureId]?.usageCount >= configs[featureId]?.usageLimit;
};

// Track token usage for a feature
export const trackFeatureTokenUsage = (
  configs: FeatureModelConfigMap,
  featureId: FeatureId,
  tokenCount: number
): FeatureModelConfigMap => {
  // Skip tracking for recommended feature to avoid hitting the limit
  if (featureId === 'recommended') {
    return configs;
  }
  
  return {
    ...configs,
    [featureId]: {
      ...configs[featureId],
      usageCount: configs[featureId].usageCount + tokenCount
    }
  };
};

// Reset all usage counts
export const resetAllFeatureUsageCounts = (
  configs: FeatureModelConfigMap
): FeatureModelConfigMap => {
  const today = new Date().toISOString();
  const newConfigs = { ...configs };
  
  Object.keys(newConfigs).forEach(key => {
    newConfigs[key as FeatureId] = {
      ...newConfigs[key as FeatureId],
      usageCount: 0,
      lastReset: today
    };
  });
  
  toast.success('All usage counters have been reset');
  return newConfigs;
};
